import React, { createContext, useContext, useReducer, useEffect } from "react";
import {
  AuthContextType,
  AuthState,
  LoginCredentials,
  User,
} from "../types/auth";
import { authService } from "../services/authService";
import toast from "react-hot-toast";

// Initial state
const initialState: AuthState = {
  user: null,
  token: localStorage.getItem("authToken"),
  isAuthenticated: false,
  isLoading: false,
  error: null,
};

// Action types
type AuthAction =
  | { type: "AUTH_START" }
  | { type: "AUTH_SUCCESS"; payload: { user: User; token: string } }
  | { type: "AUTH_FAILURE"; payload: string }
  | { type: "AUTH_LOGOUT" }
  | { type: "CLEAR_ERROR" }
  | { type: "SET_LOADING"; payload: boolean };

// Reducer
const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case "AUTH_START":
      return {
        ...state,
        isLoading: true,
        error: null,
      };
    case "AUTH_SUCCESS":
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      };
    case "AUTH_FAILURE":
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload,
      };
    case "AUTH_LOGOUT":
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      };
    case "CLEAR_ERROR":
      return {
        ...state,
        error: null,
      };
    case "SET_LOADING":
      return {
        ...state,
        isLoading: action.payload,
      };
    default:
      return state;
  }
};

// Create context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Provider component
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Check token validity on app start
  useEffect(() => {
    const initializeAuth = async () => {
      const token = localStorage.getItem("authToken");
      if (token) {
        try {
          const isValid = await authService.verifyToken();
          if (isValid) {
            const user = await authService.getProfile();
            dispatch({
              type: "AUTH_SUCCESS",
              payload: { user, token },
            });
          } else {
            localStorage.removeItem("authToken");
            dispatch({ type: "AUTH_LOGOUT" });
          }
        } catch (error) {
          localStorage.removeItem("authToken");
          dispatch({ type: "AUTH_LOGOUT" });
        }
      } else {
        dispatch({ type: "SET_LOADING", payload: false });
      }
    };

    initializeAuth();
  }, []);

  // Auto-logout when token expires
  useEffect(() => {
    const token = localStorage.getItem("authToken");
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split(".")[1]));
        const expirationTime = payload.exp * 1000; // Convert to milliseconds
        const currentTime = Date.now();

        if (expirationTime <= currentTime) {
          // Token has expired
          localStorage.removeItem("authToken");
          dispatch({ type: "AUTH_LOGOUT" });
          toast.error("Session expired. Please login again.");
        } else {
          // Set timeout to logout when token expires
          const timeUntilExpiry = expirationTime - currentTime;
          const timeoutId = setTimeout(() => {
            localStorage.removeItem("authToken");
            dispatch({ type: "AUTH_LOGOUT" });
            toast.error("Session expired. Please login again.");
          }, timeUntilExpiry);

          return () => clearTimeout(timeoutId);
        }
      } catch (error) {
        // Invalid token format
        localStorage.removeItem("authToken");
        dispatch({ type: "AUTH_LOGOUT" });
        toast.error("Invalid session. Please login again.");
      }
    }
  }, []); // Remove state.token dependency

  const login = React.useCallback(
    async (credentials: LoginCredentials) => {
      try {
        dispatch({ type: "AUTH_START" });
        const response = await authService.login(credentials);

        localStorage.setItem("authToken", response.access_token);
        dispatch({
          type: "AUTH_SUCCESS",
          payload: { user: response.user, token: response.access_token },
        });

        toast.success("Login successful!");
      } catch (error: any) {
        const errorMessage =
          error.response?.data?.message || "Login failed. Please try again.";
        dispatch({ type: "AUTH_FAILURE", payload: errorMessage });
        toast.error(errorMessage);
      }
    },
    [dispatch],
  );

  const logout = React.useCallback(() => {
    localStorage.removeItem("authToken");
    dispatch({ type: "AUTH_LOGOUT" });
    toast.success("Logged out successfully");
  }, [dispatch]);

  const clearError = React.useCallback(() => {
    dispatch({ type: "CLEAR_ERROR" });
  }, [dispatch]);

  const verifyToken = React.useCallback(async (): Promise<boolean> => {
    try {
      const isValid = await authService.verifyToken();
      return isValid;
    } catch (error) {
      return false;
    }
  }, []);

  const value: AuthContextType = React.useMemo(
    () => ({
      ...state,
      login,
      logout,
      clearError,
      verifyToken,
    }),
    [state, login, logout, clearError, verifyToken],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
